import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Certificate, CertificateDetails, CoverageStatus, InsuranceSettings, InsuranceSummary, UnitCoverage } from '@syndiqia/insurance/contracts';
import { errorMessage, useApi, useAuthHeaders } from '../api';
import { Alert, Empty, Field, Loading, PageHeader, toNumber } from '../components/ui';
import { useI18n } from '../i18n';
import { useSyndicate } from '../session';

export function useInsurance<T>(key: unknown[], path: string, enabled = true) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'insurance', ...key], queryFn: () => api<T>(`${s.api}/insurance${path}`), enabled });
}

export function useInsuranceMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  const api = useApi();
  const s = useSyndicate();
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: (input: Input) => request(api, `${s.api}/insurance`, input), onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'insurance'] }) });
}

/** Upload a certificate (the agent reads it); for oneself, or for someone (board). */
export function useUploadCertificate() {
  const authHeaders = useAuthHeaders();
  return useInsuranceMutation<{ file: File; partyId?: string; kind?: 'owner_liability' | 'occupant' }, Certificate>(async (_api, base, { file, partyId, kind }) => {
    const form = new FormData();
    if (partyId) form.append('partyId', partyId);
    if (kind) form.append('kind', kind);
    form.append('file', file);
    const res = await fetch(`${base}/certificates`, { method: 'POST', headers: authHeaders(), body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw Object.assign(new Error(body?.message ?? 'Upload failed'), { status: res.status, body });
    return body as Certificate;
  });
}

export function useOpenCertificate() {
  const s = useSyndicate();
  const authHeaders = useAuthHeaders();
  return async (id: string) => {
    const tab = window.open('', '_blank');
    const res = await fetch(`${s.api}/insurance/certificates/${id}/file`, { headers: authHeaders() });
    if (!res.ok) return tab?.close();
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
  };
}

const STATUS_CLASS: Record<CoverageStatus, string> = {
  valid: 'badge-ins-valid',
  expiring: 'badge-ins-warn',
  expired: 'badge-ins-bad',
  missing: 'badge-ins-bad',
  insufficient: 'badge-ins-bad',
  submitted: 'badge-ins-review',
};

export function InsurancePage() {
  const { t, money } = useI18n();
  const s = useSyndicate();
  const ins = t.insurance;
  const summary = useInsurance<InsuranceSummary>(['summary'], '/summary', s.can('insurance.manage'));
  const settings = useInsurance<InsuranceSettings>(['settings'], '/settings');
  const request = useInsuranceMutation<void, { sent: number; unreachable: number }>((api, base) => api(`${base}/request-missing`, { method: 'POST' }));
  if (!s.can('insurance.manage')) return <Alert tone="info">{t.common.forbidden}</Alert>;
  return (
    <>
      <PageHeader title={ins.title} intro={ins.intro} />
      {summary.data && settings.data && (
        <div className="panel ins-summary">
          <p>
            <strong>{ins.summary(summary.data.missing, summary.data.expired, summary.data.expiring)}</strong>
            {summary.data.insufficient > 0 && ` · ${summary.data.insufficient} ${ins.statuses.insufficient.toLowerCase()}`}
          </p>
          <p className="muted small">
            {ins.required} : {money(settings.data.requiredLiabilityCents)}
          </p>
          <div className="form-actions">
            <button type="button" className="button button-primary" disabled={request.isPending || summary.data.missing === 0} onClick={() => request.mutate()}>
              ✉ {ins.requestMissing}
            </button>
          </div>
          <p className="muted small">{ins.requestHint}</p>
          {request.data && <Alert tone="info">{ins.requested(request.data.sent, request.data.unreachable)}</Alert>}
          {request.isError && <Alert>{errorMessage(request.error, t)}</Alert>}
        </div>
      )}
      <ReviewQueue />
      <CoverageTable />
      {settings.data && <SettingsForm settings={settings.data} />}
    </>
  );
}

function ReviewQueue() {
  const { t } = useI18n();
  const ins = t.insurance;
  const queue = useInsurance<Certificate[]>(['certificates', 'submitted'], '/certificates?status=submitted');
  return (
    <section>
      <h2>{ins.toReview}</h2>
      {queue.data?.length === 0 && <Empty>{ins.noneToReview}</Empty>}
      {queue.data?.map((c) => (
        <CertificateCard key={c.id} certificate={c} review />
      ))}
    </section>
  );
}

/** A certificate's details (editable while awaiting review), and the board's decision. */
export function CertificateCard({ certificate: c, review = false }: { certificate: Certificate; review?: boolean }) {
  const { t, money, date } = useI18n();
  const ins = t.insurance;
  const open = useOpenCertificate();
  const editable = c.status === 'submitted';
  const [values, setValues] = useState({ insurer: c.insurer, policyNumber: c.policyNumber, liability: c.liabilityCents ? String(c.liabilityCents / 100) : '', startsOn: c.startsOn ?? '', expiresOn: c.expiresOn ?? '' });
  const [reason, setReason] = useState('');
  const save = useInsuranceMutation<CertificateDetails, Certificate>((api, base, input) => api(`${base}/certificates/${c.id}`, { method: 'PATCH', body: input }));
  const decide = useInsuranceMutation<{ outcome: 'accepted' | 'rejected'; reason: string }, Certificate>((api, base, input) => api(`${base}/certificates/${c.id}/review`, { method: 'POST', body: input }));
  const details = (): CertificateDetails => ({
    kind: c.kind,
    unitId: c.unitId,
    insurer: values.insurer,
    policyNumber: values.policyNumber,
    liabilityCents: values.liability ? Math.round((toNumber(values.liability) ?? 0) * 100) : null,
    startsOn: values.startsOn || null,
    expiresOn: values.expiresOn || null,
  });
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const accept = () => save.mutate(details(), { onSuccess: () => decide.mutate({ outcome: 'accepted', reason: '' }) });

  return (
    <div className="panel cert-card">
      <header className="dash-card-header">
        <strong>
          {c.partyName} · <span className={`badge badge-cert-${c.status}`}>{ins.certStatuses[c.status]}</span>
        </strong>
        <button type="button" className="link-button small" onClick={() => void open(c.id)}>
          📎 {ins.file}
        </button>
      </header>
      {c.status === 'rejected' && c.rejectionReason && <p className="small field-error">{ins.rejectedBecause(c.rejectionReason)}</p>}
      {editable ? (
        <>
          {(c.insurer || c.expiresOn) && <p className="muted small">{ins.readByAgent}</p>}
          <div className="row">
            <Field label={ins.insurer}>
              <input value={values.insurer} onChange={set('insurer')} />
            </Field>
            <Field label={ins.policy}>
              <input value={values.policyNumber} onChange={set('policyNumber')} />
            </Field>
            <Field label={`${ins.liability} ($)`}>
              <input inputMode="decimal" value={values.liability} onChange={set('liability')} />
            </Field>
          </div>
          <div className="row">
            <Field label={ins.startsOn}>
              <input type="date" value={values.startsOn} onChange={set('startsOn')} />
            </Field>
            <Field label={ins.expiresOn}>
              <input type="date" value={values.expiresOn} onChange={set('expiresOn')} />
            </Field>
          </div>
          <div className="form-actions">
            {review ? (
              <>
                <button type="button" className="button button-primary button-small" disabled={save.isPending || decide.isPending || !values.expiresOn} onClick={accept}>
                  {ins.accept}
                </button>
                <input className="reject-reason" placeholder={ins.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
                <button type="button" className="button button-small" disabled={decide.isPending || !reason.trim()} onClick={() => decide.mutate({ outcome: 'rejected', reason })}>
                  {ins.reject}
                </button>
              </>
            ) : (
              <button type="button" className="button button-small" disabled={save.isPending} onClick={() => save.mutate(details())}>
                {t.common.save}
              </button>
            )}
          </div>
          {(save.isError || decide.isError) && <Alert>{errorMessage(save.error ?? decide.error, t)}</Alert>}
          {save.isSuccess && !review && <p className="muted small">{t.members.saved}</p>}
        </>
      ) : (
        <p className="small">
          {[c.insurer, c.policyNumber, c.liabilityCents ? `${ins.liability} ${money(c.liabilityCents)}` : null, c.expiresOn ? `${ins.expiresOn} ${date(c.expiresOn)}` : null].filter(Boolean).join(' · ')}
        </p>
      )}
    </div>
  );
}

function CoverageTable() {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const ins = t.insurance;
  const coverage = useInsurance<UnitCoverage[]>(['coverage'], '/coverage');
  const upload = useUploadCertificate();
  const [filter, setFilter] = useState<'all' | 'problems'>('problems');
  if (coverage.isPending) return <Loading label={t.loading} />;
  const rows = (coverage.data ?? []).flatMap((u) => u.people.map((p) => ({ unit: u, p }))).filter(({ p }) => filter === 'all' || p.status !== 'valid');
  return (
    <section>
      <header className="dash-card-header">
        <h2>{ins.coverage}</h2>
        <select value={filter} onChange={(e) => setFilter(e.target.value as 'all' | 'problems')}>
          <option value="problems">{ins.statuses.missing} / {ins.statuses.expired} / …</option>
          <option value="all">{t.requests.filters.all}</option>
        </select>
      </header>
      {upload.isError && <Alert>{(upload.error as Error).message}</Alert>}
      <table className="table table-stack">
        <thead>
          <tr>
            <th>{ins.unit}</th>
            <th>{ins.person}</th>
            <th>{ins.insurer}</th>
            <th>{ins.expiresOn}</th>
            <th className="num">{ins.liability}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ unit, p }) => (
            <tr key={`${unit.unitId}-${p.partyId}-${p.role}`}>
              <td className="cell-title">
                <Link to={`${s.base}/units/${unit.unitId}`}>{unit.unitLabel}</Link>
              </td>
              <td data-label={ins.person}>
                <Link to={`${s.base}/parties/${p.partyId}`}>{p.name}</Link>
                {p.role === 'occupant' && <span className="muted small"> · {ins.roles.occupant}</span>} <span className={`badge ${STATUS_CLASS[p.status]}`}>{ins.statuses[p.status]}</span>
              </td>
              <td data-label={ins.insurer}>{p.certificate?.insurer || '—'}</td>
              <td data-label={ins.expiresOn}>{p.certificate?.expiresOn ? date(p.certificate.expiresOn) : '—'}</td>
              <td data-label={ins.liability} className="num">
                {p.certificate?.liabilityCents ? money(p.certificate.liabilityCents) : '—'}
              </td>
              <td className="cell-actions">
                <label className="link-button small upload-label">
                  {ins.uploadFor}
                  <input type="file" hidden accept="application/pdf,image/*" onChange={(e) => e.target.files?.[0] && upload.mutate({ file: e.target.files[0], partyId: p.partyId, kind: p.role === 'occupant' ? 'occupant' : 'owner_liability' })} />
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function SettingsForm({ settings }: { settings: InsuranceSettings }) {
  const { t } = useI18n();
  const ins = t.insurance;
  const [amount, setAmount] = useState(String(settings.requiredLiabilityCents / 100));
  const [occupants, setOccupants] = useState(settings.occupantsRequired);
  const save = useInsuranceMutation<{ requiredLiabilityCents: number; occupantsRequired: boolean }, InsuranceSettings>((api, base, input) => api(`${base}/settings`, { method: 'PUT', body: input }));
  return (
    <form
      className="panel form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ requiredLiabilityCents: Math.round((toNumber(amount) ?? 0) * 100), occupantsRequired: occupants });
      }}
    >
      <h2>{ins.settings}</h2>
      <Field label={`${ins.required} ($)`}>
        <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <label className="inline-field">
        <input type="checkbox" checked={occupants} onChange={(e) => setOccupants(e.target.checked)} />
        {ins.occupantsRequired}
      </label>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      {save.isSuccess && <p className="muted small">{t.members.saved}</p>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
      </div>
    </form>
  );
}
